import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, Link } from 'react-router-dom';
import { Slider } from '../components/ui/slider';
import { Navbar } from '../components/Navbar';
import { Footer } from '../components/Footer';
import { useAuth } from '../context/AuthContext';
import { API } from '../lib/api';
import {
  MapPin,
  Ruler,
  FileText,
  Calculator,
  Users,
  Palette,
  Check,
  X,
  Star,
  ArrowRight,
  Shield,
  Zap,
} from 'lucide-react';

export default function Landing() {
  const navigate = useNavigate();
  const { isAuthenticated, openAuthModal } = useAuth();
  const [testimonials, setTestimonials] = useState([]);
  const [roiData, setRoiData] = useState(null);
  const [jobsPerMonth, setJobsPerMonth] = useState(10);
  const [roofCount, setRoofCount] = useState(15);

  useEffect(() => {
    loadTestimonials();
    calculateROI(10);
  }, []);

  const loadTestimonials = async () => {
    try {
      const response = await API.get('/testimonials');
      setTestimonials(response.data.testimonials);
    } catch (error) {
      console.error('Error loading testimonials:', error);
    }
  };

  const calculateROI = async (jobs) => {
    try {
      const response = await API.post('/calculator/roi', { jobs_per_month: jobs, competitor_price: 50 });
      setRoiData(response.data);
    } catch (error) {
      console.error('Error calculating ROI:', error);
    }
  };

  const handleJobsChange = (value) => {
    const jobs = value[0];
    setJobsPerMonth(jobs);
    calculateROI(jobs);
  };

  const handleGetStarted = () => {
    if (isAuthenticated) {
      navigate('/dashboard');
    } else {
      openAuthModal('register');
    }
  };

  const features = [
    {
      icon: Calculator,
      title: 'Shingle, Tile & Metal',
      description: 'Pick the roof material and the list adjusts — tiles by the piece, metal by the panel, shingles by the bundle.',
    },
    {
      icon: Ruler,
      title: 'Valley & Ridge Takeoffs',
      description: 'Draw valleys and ridges on the roof and get the linear footage right on your report.',
    },
    {
      icon: FileText,
      title: 'Editable Material Lists',
      description: 'Edit any quantity and add your own line items — pipe boots, ridge vent, anything the job needs.',
    },
    {
      icon: Zap,
      title: 'Job Cost Estimator',
      description: 'Price materials and add labor, dumpster, and permits to cost the whole job. Private to you.',
    },
    {
      icon: Users,
      title: 'Shareable Client Reports',
      description: 'Send a clean, professional report link or PDF straight to the homeowner.',
    },
    {
      icon: Palette,
      title: 'Your Branding',
      description: 'White-label reports with your logo and company info. Work that looks like yours.',
    },
  ];

  const comparisonRows = [
    { label: 'Price per Report', sc: 'Included*', ev: '$40-75', roofr: '$25-40', qs: '$15-30', scWins: true },
    { label: 'Monthly Cost (Unlimited)', sc: '$79/mo all-in', ev: '$99+/mo', roofr: '$89/mo', qs: '$49/mo', scWins: true },
    { label: 'Turnaround', sc: 'Instant', ev: '2-24 hrs', roofr: '1-6 hrs', qs: 'Manual', scWins: true },
    { label: 'Measurement detail', sc: 'Estimate-grade', ev: 'Certified detail', roofr: 'Estimate-grade', qs: 'Varies', scWins: false },
    { label: 'PDF Reports', sc: true, ev: true, roofr: true, qs: false, scWins: false },
    { label: 'Material Lists', sc: true, ev: true, roofr: false, qs: false, scWins: true },
  ];

  const renderCell = (value, highlight = false) => {
    if (typeof value === 'boolean') {
      return value ? (
        <Check className={`w-5 h-5 mx-auto ${highlight ? 'text-orange-400' : 'text-green-400'}`} />
      ) : (
        <X className="w-5 h-5 mx-auto text-red-400/60" />
      );
    }
    return (
      <span className={highlight ? 'text-orange-400 font-semibold' : 'text-slate-400'}>
        {value}
      </span>
    );
  };

  return (
    <div className="min-h-screen bg-slate-950">
      <Helmet>
        <title>Roof Measurement Software for Shingle, Tile &amp; Metal | SquareCount</title>
        <meta name="description" content="Satellite roof measurement software with editable material lists for shingle, tile, and metal. Valley &amp; ridge takeoffs, job cost estimator, and shareable client reports. Up to 90% less than EagleView — free 7-day Contractor trial." />
        <link rel="canonical" href="https://squarecount.io/" />
        <meta property="og:title" content="Roof Measurement Software for Shingle, Tile &amp; Metal | SquareCount" />
        <meta property="og:description" content="Editable material lists for shingle, tile &amp; metal, valley/ridge takeoffs, and built-in job costing. Estimate-grade satellite measurements — up to 90% less than EagleView." />
        <meta property="og:url" content="https://squarecount.io/" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "WebApplication",
          "name": "SquareCount",
          "url": "https://squarecount.io",
          "applicationCategory": "BusinessApplication",
          "applicationSubCategory": "Roofing Measurement Software",
          "operatingSystem": "Web Browser",
          "browserRequirements": "Requires JavaScript. Works in Chrome, Firefox, Safari, Edge.",
          "description": "Instant satellite-based roof measurements for roofing contractors. Get accurate roof area, pitch, and material lists in seconds. The $79/month Contractor plan includes unlimited measurements, editable takeoffs, job costing, and branded reports.",
          "offers": {
            "@type": "AggregateOffer",
            "lowPrice": "0",
            "highPrice": "79",
            "priceCurrency": "USD",
            "offerCount": "3"
          },
          "featureList": [
            "Satellite-based roof measurements",
            "Automatic pitch detection",
            "Material and labor estimates",
            "PDF report generation",
            "Team collaboration",
            "Custom branding"
          ],
          "creator": {
            "@type": "Organization",
            "name": "SquareCount.io",
            "url": "https://squarecount.io"
          }
        })}</script>
      </Helmet>

      <Navbar />

      {/* ========== HERO ========== */}
      <section className="section-dark hero-gradient-mesh">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 pt-16 pb-16 sm:pt-24 sm:pb-20 lg:pt-32 lg:pb-28">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            {/* Left column */}
            <div className="animate-fade-in">
              <div className="inline-flex items-center gap-2 border border-orange-500/30 bg-orange-500/10 rounded-full px-4 py-1.5 mb-8">
                <Shield className="w-4 h-4 text-orange-400" />
                <span className="text-orange-300 text-sm font-medium">Trusted by Roofing Contractors Nationwide</span>
              </div>

              <h1 className="font-heading text-3xl sm:text-4xl lg:text-display font-bold text-white mb-4 animate-slide-up">
                Roof Measurement Software for Shingle, Tile &amp; Metal
              </h1>
              <p className="font-heading text-xl sm:text-2xl lg:text-3xl text-slate-300 mb-6 animate-slide-up" style={{ animationDelay: '0.05s' }}>
                Measure any roof. Build the material list. <span className="text-gradient">Price the job.</span>
              </p>

              <p className="text-lg text-slate-400 max-w-xl mb-8 animate-slide-up" style={{ animationDelay: '0.1s' }}>
                Satellite roof measurements and editable material lists for shingle, tile, or metal — with valley &amp; ridge takeoffs, a built-in job cost estimator, and client-ready reports. Estimate-grade, up to 90% less than EagleView — and every feature is free for your first 7 days.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-4 animate-slide-up" style={{ animationDelay: '0.15s' }}>
                <div className="flex flex-col items-center sm:items-start">
                  <button
                    onClick={handleGetStarted}
                    className="btn-primary inline-flex items-center justify-center gap-2 text-base px-8 py-3"
                    data-testid="hero-cta-btn"
                  >
                    Start Your 7-Day Contractor Trial
                    <ArrowRight className="w-4 h-4" />
                  </button>
                  <span className="text-sm text-slate-500 mt-2">No credit card required</span>
                </div>
                <button
                  onClick={() => {
                    const el = document.getElementById('how-it-works');
                    if (el) el.scrollIntoView({ behavior: 'smooth' });
                  }}
                  className="btn-secondary inline-flex items-center justify-center gap-2 text-base"
                >
                  See How It Works
                </button>
              </div>

              <p className="text-sm text-slate-400 animate-slide-up flex items-center gap-2" style={{ animationDelay: '0.2s' }}>
                <Users className="w-4 h-4 text-orange-400" />
                Contractors cut report costs ~90% in their first month
              </p>
            </div>

            {/* Right column -- product preview with satellite + report */}
            <div className="hidden lg:block animate-slide-up relative" style={{ animationDelay: '0.2s' }}>
              <div className="absolute -inset-4 bg-orange-500/10 rounded-3xl blur-3xl" />

              {/* Main measurement tool mockup */}
              <div className="card-glass gradient-border p-1.5 relative">
                <div className="rounded-xl bg-slate-900 overflow-hidden">
                  {/* Browser chrome */}
                  <div className="flex items-center gap-2 px-4 py-2.5 border-b border-slate-800/80">
                    <div className="flex gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-red-500/60" />
                      <span className="w-2.5 h-2.5 rounded-full bg-yellow-500/60" />
                      <span className="w-2.5 h-2.5 rounded-full bg-green-500/60" />
                    </div>
                    <div className="flex-1 text-center">
                      <span className="text-xs text-slate-500 bg-slate-800/80 px-3 py-1 rounded-md font-mono">squarecount.io/measure</span>
                    </div>
                  </div>

                  {/* App UI */}
                  <div className="p-4 space-y-3">
                    {/* Address bar */}
                    <div className="flex items-center gap-3 bg-slate-800/80 rounded-lg px-4 py-2.5 border border-slate-700/50">
                      <MapPin className="w-4 h-4 text-orange-400 shrink-0" />
                      <span className="text-slate-300 text-sm">4521 Maple Ridge Dr, Dallas, TX 75229</span>
                      <span className="ml-auto text-xs bg-green-500/20 text-green-400 px-2 py-0.5 rounded-full">Found</span>
                    </div>

                    {/* Satellite view with house and roof outline */}
                    <div className="relative rounded-lg overflow-hidden border border-slate-700/30">
                      {/* Satellite background - realistic green/brown terrain */}
                      <div className="aspect-[16/10] relative">
                        <div className="absolute inset-0" style={{
                          background: `
                            radial-gradient(ellipse 30% 25% at 50% 45%, #2d4a2d 0%, transparent 100%),
                            radial-gradient(ellipse 40% 35% at 45% 50%, #3a5a3a 0%, transparent 100%),
                            radial-gradient(ellipse 20% 15% at 70% 30%, #4a3a2a 0%, transparent 100%),
                            radial-gradient(ellipse 15% 10% at 30% 65%, #3a4a3a 0%, transparent 100%),
                            linear-gradient(135deg, #2a3a2a 0%, #1a2a1a 50%, #2a3a2a 100%)
                          `
                        }} />

                        {/* House shape - realistic L-shaped roof */}
                        <svg viewBox="0 0 400 250" className="absolute inset-0 w-full h-full">
                          {/* Driveway */}
                          <rect x="280" y="140" width="40" height="80" fill="#4a4a4a" opacity="0.3" rx="2" />

                          {/* House shadow */}
                          <polygon points="110,80 200,50 290,80 310,75 310,170 280,170 280,130 220,130 220,170 110,170"
                            fill="#000" opacity="0.2" transform="translate(4, 4)" />

                          {/* Main roof section */}
                          <polygon points="110,80 200,50 290,80 290,170 110,170"
                            fill="#5a4a3a" stroke="#f97316" strokeWidth="2.5" opacity="0.8" />

                          {/* Garage/wing section */}
                          <polygon points="290,75 340,65 340,140 290,140"
                            fill="#4a3a2a" stroke="#f97316" strokeWidth="2.5" opacity="0.8" />

                          {/* Ridge line */}
                          <line x1="200" y1="50" x2="200" y2="170" stroke="#f97316" strokeWidth="1.5" strokeDasharray="6,3" opacity="0.6" />
                          <line x1="315" y1="65" x2="315" y2="140" stroke="#f97316" strokeWidth="1.5" strokeDasharray="6,3" opacity="0.6" />

                          {/* Measurement vertices */}
                          <circle cx="110" cy="80" r="5" fill="#f97316" />
                          <circle cx="200" cy="50" r="5" fill="#f97316" />
                          <circle cx="290" cy="80" r="5" fill="#f97316" />
                          <circle cx="340" cy="65" r="5" fill="#f97316" />
                          <circle cx="340" cy="140" r="5" fill="#f97316" />
                          <circle cx="290" cy="170" r="5" fill="#f97316" />
                          <circle cx="110" cy="170" r="5" fill="#f97316" />

                          {/* Dimension labels */}
                          <rect x="125" y="55" width="56" height="18" rx="4" fill="#0f172a" opacity="0.85" />
                          <text x="153" y="68" textAnchor="middle" fill="#f97316" fontSize="10" fontFamily="monospace">42.3 ft</text>

                          <rect x="85" y="115" width="50" height="18" rx="4" fill="#0f172a" opacity="0.85" />
                          <text x="110" y="128" textAnchor="middle" fill="#f97316" fontSize="10" fontFamily="monospace">38.1 ft</text>

                          <rect x="295" y="92" width="50" height="18" rx="4" fill="#0f172a" opacity="0.85" />
                          <text x="320" y="105" textAnchor="middle" fill="#f97316" fontSize="10" fontFamily="monospace">24.6 ft</text>
                        </svg>

                        {/* Map controls */}
                        <div className="absolute top-3 right-3 flex flex-col gap-1">
                          <button className="w-7 h-7 bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 rounded text-white text-xs flex items-center justify-center">+</button>
                          <button className="w-7 h-7 bg-slate-900/80 backdrop-blur-sm border border-slate-700/50 rounded text-white text-xs flex items-center justify-center">−</button>
                        </div>

                        {/* Satellite label */}
                        <div className="absolute bottom-2 left-2">
                          <span className="text-[10px] text-white/50 bg-slate-900/60 px-1.5 py-0.5 rounded">Satellite View</span>
                        </div>
                      </div>
                    </div>

                    {/* Results panel */}
                    <div className="grid grid-cols-4 gap-2">
                      <div className="bg-slate-800/60 border border-slate-700/30 rounded-lg p-2.5 text-center">
                        <p className="text-[10px] text-slate-500 mb-0.5">Total Area</p>
                        <p className="text-orange-400 font-heading font-bold text-base">24.3 sq</p>
                      </div>
                      <div className="bg-slate-800/60 border border-slate-700/30 rounded-lg p-2.5 text-center">
                        <p className="text-[10px] text-slate-500 mb-0.5">Pitch</p>
                        <p className="text-white font-heading font-bold text-base">6/12</p>
                      </div>
                      <div className="bg-slate-800/60 border border-slate-700/30 rounded-lg p-2.5 text-center">
                        <p className="text-[10px] text-slate-500 mb-0.5">Waste</p>
                        <p className="text-white font-heading font-bold text-base">12%</p>
                      </div>
                      <div className="bg-slate-800/60 border border-slate-700/30 rounded-lg p-2.5 text-center">
                        <p className="text-[10px] text-slate-500 mb-0.5">Perimeter</p>
                        <p className="text-white font-heading font-bold text-base">196 ft</p>
                      </div>
                    </div>

                    {/* Material estimate preview */}
                    <div className="bg-slate-800/40 border border-slate-700/30 rounded-lg p-3">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs text-slate-400 font-medium">Material Estimate</span>
                        <span className="text-[10px] bg-orange-500/20 text-orange-400 px-2 py-0.5 rounded-full">Auto-calculated</span>
                      </div>
                      <div className="grid grid-cols-2 gap-x-6 gap-y-1.5 text-xs">
                        <div className="flex justify-between"><span className="text-slate-500">Shingles</span><span className="text-slate-300 font-medium">76 bundles</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Underlayment</span><span className="text-slate-300 font-medium">7 rolls</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Ridge Cap</span><span className="text-slate-300 font-medium">4 bundles</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Drip Edge</span><span className="text-slate-300 font-medium">216 ft</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Starter Strip</span><span className="text-slate-300 font-medium">6 bundles</span></div>
                        <div className="flex justify-between"><span className="text-slate-500">Nails</span><span className="text-slate-300 font-medium">62 lbs</span></div>
                      </div>
                    </div>

                    {/* Action buttons */}
                    <div className="flex gap-2">
                      <button className="flex-1 bg-orange-500 text-white text-sm font-semibold py-2 rounded-lg flex items-center justify-center gap-1.5">
                        <FileText className="w-3.5 h-3.5" />
                        Generate PDF Report
                      </button>
                      <button className="px-4 bg-slate-800 text-slate-300 text-sm font-medium py-2 rounded-lg border border-slate-700/50">
                        Save
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ========== SOCIAL PROOF BAR ========== */}
      <section className="section-darker border-y border-slate-800/60">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8 max-w-2xl mx-auto text-center">
            <div>
              <p className="stat-value text-2xl">60s</p>
              <p className="text-xs text-slate-500 mt-1">Average Report Time</p>
            </div>
            <div>
              <p className="stat-value text-2xl">Estimate-grade</p>
              <p className="text-xs text-slate-500 mt-1">Satellite Measurements</p>
            </div>
            <div>
              <p className="stat-value text-2xl">90%</p>
              <p className="text-xs text-slate-500 mt-1">Less Than EagleView</p>
            </div>
          </div>
        </div>
      </section>

      {/* ========== SAVINGS CALCULATOR (ABOVE THE FOLD) ========== */}
      <section className="py-12 px-4">
        <div className="max-w-3xl mx-auto">
          <div className="card-glass gradient-border p-8 text-center">
            <h2 className="text-2xl font-heading font-bold text-white mb-2">How Much Are You Wasting on Roof Reports?</h2>
            <p className="text-slate-400 mb-6">Enter your monthly volume to see your savings</p>
            <div className="flex items-center justify-center gap-4 mb-6 flex-wrap">
              <span className="text-slate-400">I measure</span>
              <input
                type="number"
                min="1"
                max="200"
                value={roofCount}
                onChange={(e) => {
                  const val = Math.max(1, Math.min(200, parseInt(e.target.value) || 1));
                  setRoofCount(val);
                }}
                className="w-20 bg-slate-800 border border-slate-600 rounded-lg px-3 py-2 text-white text-center text-xl font-bold focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent"
              />
              <span className="text-slate-400">roofs per month</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div className="p-4 bg-red-500/10 rounded-lg border border-red-500/20">
                <p className="text-red-400 text-sm">EagleView Cost</p>
                <p className="text-red-400 text-2xl font-bold">${(roofCount * 50).toLocaleString()}/mo</p>
              </div>
              <div className="p-4 bg-slate-800 rounded-lg">
                <p className="text-slate-400 text-sm">SquareCount Contractor</p>
                <p className="text-white text-2xl font-bold">$79/mo</p>
              </div>
              <div className="p-4 bg-green-500/10 rounded-lg border border-green-500/20">
                <p className="text-green-400 text-sm">You Save</p>
                <p className="text-green-400 text-2xl font-bold">${Math.max(roofCount * 50 - 79, 0).toLocaleString()}/mo</p>
              </div>
            </div>
            <button onClick={() => openAuthModal('register')} className="btn-primary mt-6 px-8 py-3 text-lg">
              Start Your 7-Day Contractor Trial
            </button>
          </div>
        </div>
      </section>

      {/* ========== HOW IT WORKS ========== */}
      <section id="how-it-works" className="section-dark py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-display-sm font-bold text-white mb-4">
              How It Works
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Three simple steps from address to accurate roof report.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                num: '1',
                title: 'Search Any Address',
                desc: 'Type in any U.S. address and our satellite imagery loads instantly. No scheduling, no waiting.',
                icon: MapPin,
              },
              {
                num: '2',
                title: 'Draw Your Roof Outline',
                desc: 'Click to trace the roof edges on the satellite view. Our smart tools snap to ridgelines automatically.',
                icon: Ruler,
              },
              {
                num: '3',
                title: 'Get Instant Measurements',
                desc: 'Receive total squares, pitch, waste factor, material lists, and a professional PDF report in seconds.',
                icon: FileText,
              },
            ].map((step) => {
              const Icon = step.icon;
              return (
                <div key={step.num} className={`card-glass-hover gradient-border p-8 animate-slide-up${step.num !== '3' ? ' step-connector' : ''}`} style={{ animationDelay: `${Number(step.num) * 0.1}s` }}>
                  <div className="flex items-center gap-4 mb-6">
                    <span className="flex-none w-10 h-10 rounded-full bg-orange-500/15 border border-orange-500/30 flex items-center justify-center font-heading text-orange-400 font-bold text-lg">
                      {step.num}
                    </span>
                    <h3 className="font-heading text-xl font-bold text-white">{step.title}</h3>
                  </div>
                  <p className="text-slate-400 leading-relaxed">{step.desc}</p>
                  <div className="mt-6 flex items-center justify-center w-12 h-12 rounded-lg bg-slate-800/60">
                    <Icon className="w-6 h-6 text-slate-500" />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ========== FEATURES GRID ========== */}
      <section className="section-darker py-20 lg:py-28">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-display-sm font-bold text-white mb-4">
              Everything You Need to Close More Jobs
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Built for roofing contractors who value speed, accuracy, and their bottom line.
              <span className="block mt-2 text-orange-400 text-base font-medium">All included in your 7-day Contractor trial.</span>
            </p>
          </div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div
                  key={feature.title}
                  className="card-glass-hover p-6 animate-slide-up"
                  style={{ animationDelay: `${index * 0.05}s` }}
                >
                  <div className="flex items-start justify-between mb-4">
                    <div className="w-10 h-10 rounded-lg bg-orange-500/10 border border-orange-500/20 flex items-center justify-center icon-glow">
                      <Icon className="w-5 h-5 text-orange-400" />
                    </div>
                    <span className="text-[10px] uppercase tracking-wide font-semibold text-orange-400 bg-orange-500/10 border border-orange-500/20 rounded-full px-2 py-0.5">
                      Contractor
                    </span>
                  </div>
                  <h3 className="font-heading text-lg font-bold text-white mb-2">{feature.title}</h3>
                  <p className="text-slate-400 text-sm leading-relaxed">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ========== COMPARISON TABLE ========== */}
      <section className="section-dark py-20 lg:py-28">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-display-sm font-bold text-white mb-4">
              See How We Compare
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Estimate-grade measurements built for roofing, at a fraction of the price.
            </p>
          </div>

          <div className="card-glass overflow-hidden animate-slide-up">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[600px]">
                <thead>
                  <tr className="border-b border-slate-800">
                    <th className="text-left text-slate-500 font-medium px-4 sm:px-6 py-4 w-1/5" />
                    <th className="px-6 py-4 text-center bg-orange-500/10 border-x border-orange-500/20">
                      <div className="flex flex-col items-center gap-1">
                        <span className="inline-block bg-orange-500 text-white text-[10px] font-bold uppercase px-2 py-0.5 rounded-full tracking-wide">Best Value</span>
                        <span className="text-gradient font-heading font-bold text-base">SquareCount</span>
                      </div>
                    </th>
                    <th className="px-6 py-4 text-center text-slate-500 font-medium">EagleView</th>
                    <th className="px-6 py-4 text-center text-slate-500 font-medium">Roofr</th>
                    <th className="px-6 py-4 text-center text-slate-500 font-medium">QuickSquares</th>
                  </tr>
                </thead>
                <tbody>
                  {comparisonRows.map((row) => (
                    <tr key={row.label} className="border-b border-slate-800/50 last:border-0">
                      <td className="px-6 py-4 text-slate-300 font-medium">{row.label}</td>
                      <td className="px-6 py-4 text-center bg-orange-500/[0.06] border-x border-orange-500/10">
                        <div className="flex items-center justify-center gap-2">
                          {renderCell(row.sc, true)}
                          {row.scWins && <Check className="w-4 h-4 text-green-400 shrink-0" />}
                        </div>
                      </td>
                      <td className="px-6 py-4 text-center">{renderCell(row.ev)}</td>
                      <td className="px-6 py-4 text-center">{renderCell(row.roofr)}</td>
                      <td className="px-6 py-4 text-center">{renderCell(row.qs)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p className="text-xs text-slate-600 px-6 py-3 border-t border-slate-800/50">
              *With the $79/mo Contractor plan — unlimited reports, every feature included. Single reports available at $9.99 on Free tier.
            </p>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ========== ROI CALCULATOR ========== */}
      <section className="section-darker py-20 lg:py-28" data-testid="roi-calculator-section">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-display-sm font-bold text-white mb-4">
              Calculate Your Savings
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              See how much you save annually compared to EagleView.
            </p>
          </div>

          <div className="card-glass gradient-border glow-orange-soft p-8 lg:p-10 animate-slide-up">
            <div className="grid md:grid-cols-2 gap-10">
              {/* Slider side */}
              <div>
                <label className="block text-sm text-slate-400 mb-5">
                  How many roof jobs do you quote per month?
                </label>
                <Slider
                  value={[jobsPerMonth]}
                  onValueChange={handleJobsChange}
                  min={1}
                  max={50}
                  step={1}
                  className="w-full"
                  data-testid="jobs-slider"
                />
                <div className="flex justify-between text-sm text-slate-500 mt-3">
                  <span>1 job</span>
                  <span className="text-orange-400 font-heading font-bold text-lg">{jobsPerMonth} jobs/mo</span>
                  <span>50 jobs</span>
                </div>

                {roiData && (
                  <div className="mt-6 bg-slate-800/60 rounded-lg p-4 border border-slate-700/50">
                    <p className="text-xs text-slate-500 mb-1">Recommended Plan</p>
                    <p className="font-heading font-bold text-white text-xl">
                      {roiData.recommended_plan}{' '}
                      <span className="text-orange-400">${roiData.squarecount_monthly}/mo</span>
                    </p>
                  </div>
                )}
              </div>

              {/* Results side */}
              {roiData && (
                <div className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="rounded-lg bg-red-500/10 border border-red-500/20 p-4">
                      <p className="text-xs text-red-400/80 mb-1">EagleView Cost</p>
                      <p className="font-heading font-bold text-red-400 text-2xl">
                        ${roiData.competitor_monthly.toLocaleString()}/mo
                      </p>
                    </div>
                    <div className="rounded-lg bg-green-500/10 border border-green-500/20 p-4">
                      <p className="text-xs text-green-400/80 mb-1">SquareCount Cost</p>
                      <p className="font-heading font-bold text-green-400 text-2xl">
                        ${roiData.squarecount_monthly}/mo
                      </p>
                    </div>
                  </div>

                  <div className="rounded-lg bg-orange-500/15 border border-orange-500/25 p-6 text-center">
                    <p className="text-orange-300/80 text-sm mb-2">Your Annual Savings</p>
                    <p className="font-heading font-black text-5xl text-orange-400">
                      ${roiData.savings_yearly.toLocaleString()}
                    </p>
                    <p className="text-orange-300/60 text-sm mt-2">
                      That's {roiData.savings_percentage}% less than EagleView
                    </p>
                  </div>

                  <button
                    onClick={handleGetStarted}
                    className="btn-primary w-full inline-flex items-center justify-center gap-2"
                    data-testid="roi-cta-btn"
                  >
                    Start Saving Today
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </section>

      <div className="section-divider" />

      {/* ========== TESTIMONIALS ========== */}
      {testimonials.length > 0 && (
      <section className="section-dark py-20 lg:py-28" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-16 animate-fade-in">
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-display-sm font-bold text-white mb-4">
              What Contractors Are Saying
            </h2>
            <p className="text-slate-400 text-lg max-w-2xl mx-auto">
              Real feedback from roofing contractors using SquareCount.
            </p>
          </div>

          <div className="flex flex-wrap justify-center gap-6">
            {testimonials.slice(0, 3).map((testimonial) => (
              <div
                key={testimonial.id}
                className="card-glass-hover p-6 animate-slide-up w-full sm:w-[360px]"
                data-testid={`testimonial-${testimonial.id}`}
              >
                <div className="flex gap-0.5 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} className="w-4 h-4 fill-orange-400 text-orange-400" />
                  ))}
                </div>
                <p className="text-slate-300 leading-relaxed mb-6 italic">
                  "{testimonial.quote}"
                </p>
                <div className="flex items-center gap-3 pt-4 border-t border-slate-800">
                  <div className="w-10 h-10 rounded-full bg-orange-500/20 border border-orange-500/30 flex items-center justify-center shrink-0">
                    <span className="text-orange-400 font-bold text-sm">{testimonial.avatar}</span>
                  </div>
                  <div>
                    <p className="font-heading font-bold text-white text-sm">{testimonial.name}</p>
                    <p className="text-xs text-slate-500">{testimonial.company}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
      )}

      <div className="section-divider" />

      {/* ========== FINAL CTA ========== */}
      <section className="section-darker py-20 lg:py-28 hero-gradient-mesh">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 text-center animate-fade-in">
          <h2 className="font-heading text-2xl sm:text-3xl lg:text-display-sm font-bold text-white mb-6">
            Ready to Save 90% on Roof Reports?
          </h2>
          <p className="text-slate-400 text-lg mb-8 max-w-xl mx-auto">
            Try every Contractor feature free for 7 days — unlimited measurements, editable takeoffs, job costing, branded reports. No credit card required.
          </p>
          <button
            onClick={handleGetStarted}
            className="btn-primary inline-flex items-center justify-center gap-2 text-lg px-8 py-4"
            data-testid="cta-bottom-btn"
          >
            Start Your 7-Day Contractor Trial
            <ArrowRight className="w-5 h-5" />
          </button>
          <p className="text-sm text-slate-500 mt-4">
            No credit card required. Cancel anytime. Keep your reports even if you don't upgrade.
          </p>
        </div>
      </section>

      {/* ========== FAQ SCHEMA ========== */}
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          {
            "@type": "Question",
            "name": "How do you measure a roof from satellite?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "SquareCount uses high-resolution satellite imagery to measure roof area, pitch, ridges, and valleys. Simply enter any US address, trace the roof outline, and get instant measurements — no ladders, drones, or site visits needed."
            }
          },
          {
            "@type": "Question",
            "name": "How much does roof measurement software cost?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "SquareCount offers 3 free satellite roof measurements per month. The Contractor plan is $79/month for unlimited measurements with editable material lists for shingle, tile, and metal, valley and ridge takeoffs, a job cost estimator, and branded PDF reports — about 90% less than EagleView's $40-75 per report pricing for a typical contractor."
            }
          },
          {
            "@type": "Question",
            "name": "Is satellite roof measurement accurate?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Satellite-based roof measurement tools like SquareCount produce estimate-grade measurements — roof area, pitch, and material requirements — from high-resolution imagery, suitable for estimating and ordering materials. For certified insurance-claim evidence, services like EagleView retain an advantage."
            }
          },
          {
            "@type": "Question",
            "name": "What is the best EagleView alternative?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "SquareCount is the top-rated EagleView alternative for roofing contractors. The $79/month Contractor plan includes unlimited satellite-based measurements with editable takeoffs and job costing — up to 90% less than EagleView's $40-75 per report — with instant results instead of 24-48 hour turnaround."
            }
          },
          {
            "@type": "Question",
            "name": "Can I get free roof measurements?",
            "acceptedAnswer": {
              "@type": "Answer",
              "text": "Yes. SquareCount offers 3 free satellite roof measurements every month — no credit card required. You get roof area, pitch calculation, and basic estimates completely free. Every new account also starts with a free 7-day trial of the full Contractor plan — unlimited reports, editable takeoffs, and job costing."
            }
          }
        ]
      }) }} />

      {/* ========== INTERNAL LINKS / RESOURCES ========== */}
      <section className="section-dark py-12 border-t border-slate-800/50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <h2 className="font-heading text-xl font-bold text-white mb-6 text-center">Explore More Tools & Resources</h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
            <Link to="/free-alternative" className="p-4 bg-slate-900/50 border border-slate-800 rounded-lg hover:border-orange-500/30 transition-colors group">
              <h3 className="font-heading font-bold text-white text-sm mb-1 group-hover:text-orange-400 transition-colors">Free Roof Measurement</h3>
              <p className="text-slate-500 text-xs">3 free reports/month. No credit card.</p>
            </Link>
            <Link to="/eagleview-alternative" className="p-4 bg-slate-900/50 border border-slate-800 rounded-lg hover:border-orange-500/30 transition-colors group">
              <h3 className="font-heading font-bold text-white text-sm mb-1 group-hover:text-orange-400 transition-colors">EagleView Alternative</h3>
              <p className="text-slate-500 text-xs">Estimate-grade, up to 90% less cost.</p>
            </Link>
            <Link to="/tools/pitch-calculator" className="p-4 bg-slate-900/50 border border-slate-800 rounded-lg hover:border-orange-500/30 transition-colors group">
              <h3 className="font-heading font-bold text-white text-sm mb-1 group-hover:text-orange-400 transition-colors">Roof Pitch Calculator</h3>
              <p className="text-slate-500 text-xs">Free pitch factor & slope calculator.</p>
            </Link>
            <Link to="/blog" className="p-4 bg-slate-900/50 border border-slate-800 rounded-lg hover:border-orange-500/30 transition-colors group">
              <h3 className="font-heading font-bold text-white text-sm mb-1 group-hover:text-orange-400 transition-colors">Roofing Blog</h3>
              <p className="text-slate-500 text-xs">Guides, tips, and industry insights.</p>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
